import React from "react";
import {
  WizardStep,
  DocumentType,
  DOCUMENT_TYPES,
  DocumentAnalysisResponse,
  InputPanelState,
  UploadedFileInfo,
} from "../types";
import { GenerationSection } from "./GenerateDraftSidebar";
import "./TreatmentPlanSidebar.css";

interface SectionInfo {
  id: GenerationSection;
  name: string;
}

const PATIENT_DATA_SECTIONS: SectionInfo[] = [
  { id: GenerationSection.DOCUMENT_HEADER, name: "Document Header" },
  { id: GenerationSection.PATIENT_INFORMATION, name: "Patient Information" },
  { id: GenerationSection.DIAGNOSTIC_INFORMATION, name: "Diagnostic Information" },
  { id: GenerationSection.HISTORY_OF_SERVICES, name: "History of Services" },
  { id: GenerationSection.RELEVANT_HISTORY, name: "Relevant History" },
  { id: GenerationSection.DIRECT_AND_INDIRECT_MEASURES, name: "Direct and Indirect Measures" },
];

const ASSESSMENT_RESULTS_SECTIONS: SectionInfo[] = [
  { id: GenerationSection.INITIAL_OBSERVATION, name: "Initial Observation" },
  { id: GenerationSection.LANGUAGE_DOMAIN, name: "Language Domain" },
  { id: GenerationSection.SOCIAL_AND_PLAY_DOMAIN, name: "Social & Play Domain" },
  { id: GenerationSection.ATTENDING_SKILLS_DOMAIN, name: "Attending Skills Domain" },
  { id: GenerationSection.DAILY_LIVING_DOMAIN, name: "Daily Living Domain" },
  { id: GenerationSection.SAFETY_SKILLS_DOMAIN, name: "Safety Skills Domain" },
  { id: GenerationSection.FUNCTIONAL_BEHAVIOR_ASSESSMENT, name: "Functional Behavior Assessment" },
];

const TREATMENT_GOALS_SECTIONS: SectionInfo[] = [
  { id: GenerationSection.CLINICAL_RECOMMENDATIONS, name: "Clinical Recommendations" },
  { id: GenerationSection.LANGUAGE_GOALS, name: "Language Goals" },
  { id: GenerationSection.SOCIAL_AND_PLAY_GOALS, name: "Social & Play Goals" },
  { id: GenerationSection.ATTENDING_GOALS, name: "Attending Goals" },
  { id: GenerationSection.DAILY_LIVING_GOALS, name: "Daily Living Goals" },
  { id: GenerationSection.SAFETY_GOALS, name: "Safety Goals" },
  { id: GenerationSection.CAREGIVER_GOALS, name: "Caregiver Goals" },
];

const SUPPORT_PLAN_SECTIONS: SectionInfo[] = [
  { id: GenerationSection.PLAN_FOR_GENERALIZATION, name: "Plan for Generalization" },
  { id: GenerationSection.TRANSITION_PLANNING, name: "Transition Planning" },
  { id: GenerationSection.CRITERIA_FOR_DISCHARGE, name: "Criteria for Discharge" },
  { id: GenerationSection.COORDINATION_OF_CARE, name: "Coordination of Care" },
  { id: GenerationSection.CRISIS_MANAGEMENT_PLANNING, name: "Crisis Management Planning" },
  { id: GenerationSection.REQUESTED_HOURS_OF_SERVICE, name: "Requested Hours of Service" },
];

const ALL_SECTIONS: SectionInfo[] = [
  ...PATIENT_DATA_SECTIONS,
  ...ASSESSMENT_RESULTS_SECTIONS,
  ...TREATMENT_GOALS_SECTIONS,
  ...SUPPORT_PLAN_SECTIONS,
];

/** Refresh icon: file was replaced; re-run analysis on Next */
const ReanalyzeIcon: React.FC = () => (
  <svg
    className="sidebar-item__reanalyze-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M23 4v6h-6" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

interface TreatmentPlanSidebarProps {
  currentStep: WizardStep;
  // For Upload Documents step
  activeDocumentType?: DocumentType;
  onDocumentTypeChange?: (type: DocumentType) => void;
  inputStateMap?: Record<DocumentType, InputPanelState>;
  analysisResultsMap?: Record<DocumentType, DocumentAnalysisResponse | null>;
  /** Filename last successfully analyzed per document (must match current file for ✓) */
  analyzedFileKeys?: Record<string, string>;
  uploadedFiles?: Record<string, UploadedFileInfo>;
  /** Doc types cleared locally but not yet synced to the server */
  pendingClears?: Set<DocumentType>;
  // For Section generation steps
  activeSection?: GenerationSection;
  onSectionChange?: (section: GenerationSection) => void;
  generatedSections?: Map<GenerationSection, any>;
  // For Review step
  onReviewSectionClick?: (section: GenerationSection) => void;
}

const TreatmentPlanSidebar: React.FC<TreatmentPlanSidebarProps> = ({
  currentStep,
  activeDocumentType,
  onDocumentTypeChange,
  inputStateMap,
  analysisResultsMap,
  analyzedFileKeys,
  uploadedFiles,
  pendingClears,
  activeSection,
  onSectionChange,
  generatedSections,
  onReviewSectionClick,
}) => {
  const getSectionsForStep = (): SectionInfo[] => {
    switch (currentStep) {
      case WizardStep.PATIENT_DATA:
        return PATIENT_DATA_SECTIONS;
      case WizardStep.ASSESSMENT_RESULTS:
        return ASSESSMENT_RESULTS_SECTIONS;
      case WizardStep.TREATMENT_GOALS:
        return TREATMENT_GOALS_SECTIONS;
      case WizardStep.SUPPORT_PLAN:
        return SUPPORT_PLAN_SECTIONS;
      default:
        return [];
    }
  };

  const getStepTitle = (): string => {
    switch (currentStep) {
      case WizardStep.UPLOAD_DOCUMENTS:
        return "Document Types";
      case WizardStep.PATIENT_DATA:
        return "Patient Data";
      case WizardStep.ASSESSMENT_RESULTS:
        return "Assessment Results";
      case WizardStep.TREATMENT_GOALS:
        return "Treatment Goals";
      case WizardStep.SUPPORT_PLAN:
        return "Support Plan";
      case WizardStep.REVIEW:
        return "Review";
      default:
        return "";
    }
  };

  const isPendingClear = (docType: DocumentType): boolean => {
    return !!pendingClears?.has(docType) && !inputStateMap?.[docType]?.file;
  };

  const hasAnyFile = (docType: DocumentType): boolean => {
    if (inputStateMap?.[docType]?.file) return true;
    if (isPendingClear(docType)) return false;
    return !!uploadedFiles?.[docType];
  };

  /** Server has a current analysis for this doc (and it's not pending clear). */
  const isAnalysisCurrent = (docType: DocumentType): boolean => {
    if (isPendingClear(docType)) return false;

    const analysis = analysisResultsMap?.[docType];
    if (analysis === null || analysis === undefined) return false;

    const localFile = inputStateMap?.[docType]?.file;
    const server = uploadedFiles?.[docType];
    const lastKey = analyzedFileKeys?.[docType];

    if (localFile) {
      if (lastKey === undefined || lastKey === "") return false;
      return lastKey === localFile.name;
    }

    if (server?.filename) {
      if (lastKey === undefined || lastKey === "") {
        return true;
      }
      return lastKey === server.filename;
    }

    return false;
  };

  const hasStaleAnalysis = (docType: DocumentType): boolean => {
    if (isPendingClear(docType)) return false;
    const analysis = analysisResultsMap?.[docType];
    return analysis !== null && analysis !== undefined && hasAnyFile(docType) && !isAnalysisCurrent(docType);
  };

  const isGenerated = (section: GenerationSection): boolean => {
    return Boolean(generatedSections?.has(section) && generatedSections.get(section) !== null);
  };

  const getUploadedCount = (): number => {
    return DOCUMENT_TYPES.filter(dt => hasAnyFile(dt.type)).length;
  };

  const getGeneratedCount = (): number => {
    const sections = getSectionsForStep();
    return sections.filter(s => isGenerated(s.id)).length;
  };

  const renderDocumentTypes = () => (
    <nav className="sidebar-nav">
      {DOCUMENT_TYPES.map((docType) => {
        const cleared = isPendingClear(docType.type);
        const uploaded = hasAnyFile(docType.type);
        const current = isAnalysisCurrent(docType.type);
        const stale = hasStaleAnalysis(docType.type);
        const pendingNew = uploaded && !current && !stale;
        const isActive = activeDocumentType === docType.type;

        return (
          <button
            key={docType.type}
            className={`sidebar-item ${isActive ? "sidebar-item--active" : ""} ${current ? "sidebar-item--completed" : uploaded ? "sidebar-item--uploaded" : ""} ${cleared ? "sidebar-item--cleared" : ""}`}
            onClick={() => onDocumentTypeChange?.(docType.type)}
          >
            <span className="sidebar-item__name">{docType.name}</span>
            {cleared && (
              <span
                className="sidebar-item__badge sidebar-item__badge--cleared"
                title="Cleared — will be removed on Next"
              >
                ✕
              </span>
            )}
            {!cleared && current && <span className="sidebar-item__check">✓</span>}
            {!cleared && stale && (
              <span
                className="sidebar-item__badge sidebar-item__badge--reanalyze"
                title="File changed — run Next to re-analyze"
              >
                <ReanalyzeIcon />
              </span>
            )}
            {!cleared && pendingNew && (
              <span className="sidebar-item__badge" title="Uploaded — run Next to analyze">
                📎
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );

  const renderSections = () => {
    const sections = getSectionsForStep();

    return (
      <nav className="sidebar-nav">
        {sections.map((section) => {
          const generated = isGenerated(section.id);
          const isActive = activeSection === section.id;

          return (
            <button
              key={section.id}
              className={`sidebar-item ${isActive ? "sidebar-item--active" : ""} ${generated ? "sidebar-item--completed" : ""}`}
              onClick={() => onSectionChange?.(section.id)}
            >
              <span className="sidebar-item__name">{section.name}</span>
              {generated && <span className="sidebar-item__check">✓</span>}
            </button>
          );
        })}
      </nav>
    );
  };

  const getReviewGeneratedCount = (): number => {
    return ALL_SECTIONS.filter(s =>
      generatedSections?.has(s.id) && generatedSections.get(s.id) !== null
    ).length;
  };

  const renderReviewTOC = () => (
    <nav className="sidebar-nav">
      {ALL_SECTIONS.map((section) => {
        const generated = generatedSections?.has(section.id) && generatedSections.get(section.id) !== null;
        return (
          <button
            key={section.id}
            className={`sidebar-item ${generated ? "sidebar-item--completed" : ""}`}
            onClick={() => generated && onReviewSectionClick?.(section.id)}
            disabled={!generated}
          >
            <span className="sidebar-item__name">{section.name}</span>
            {generated && <span className="sidebar-item__check">✓</span>}
          </button>
        );
      })}
    </nav>
  );

  return (
    <aside className="treatment-plan-sidebar">
      <div className="sidebar-header">
        <h3 className="sidebar-title">{getStepTitle()}</h3>
        {currentStep === WizardStep.UPLOAD_DOCUMENTS && (
          <span className="sidebar-progress">{getUploadedCount()}/{DOCUMENT_TYPES.length}</span>
        )}
        {currentStep >= WizardStep.PATIENT_DATA && currentStep <= WizardStep.SUPPORT_PLAN && (
          <span className="sidebar-progress">{getGeneratedCount()}/{getSectionsForStep().length}</span>
        )}
        {currentStep === WizardStep.REVIEW && (
          <span className="sidebar-progress">{getReviewGeneratedCount()}/{ALL_SECTIONS.length}</span>
        )}
      </div>

      <div className="sidebar-content">
        {currentStep === WizardStep.UPLOAD_DOCUMENTS && renderDocumentTypes()}
        {currentStep >= WizardStep.PATIENT_DATA && currentStep <= WizardStep.SUPPORT_PLAN && renderSections()}
        {currentStep === WizardStep.REVIEW && renderReviewTOC()}
      </div>
    </aside>
  );
};

export default TreatmentPlanSidebar;
